import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { BudgetClassificationCodeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find.dto';
import type { BudgetClassificationCodeFindManyQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find-many-query.dto';
import { BudgetClassificationCodeService } from '@zaoblako/web/core/services/basis/budget-classification-code.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

@Component({
  selector: 'crm-budget-classification-code-single-select',
  standalone: true,
  imports: [CommonModule, SpinnerComponent, SingleSelectComponent, SafeHtmlPipe],
  templateUrl: './budget-classification-code-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetClassificationCodeSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public budgetCodes: BudgetClassificationCodeFindInterface[] = [];
  public queryParams: BudgetClassificationCodeFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedBudgetCode: BudgetClassificationCodeFindInterface;
  @Input() public set selectedBudgetCode(selectedBudgetCode: BudgetClassificationCodeFindInterface) {
    if (this.customSearch) {
      this._selectedBudgetCode = selectedBudgetCode;
      return;
    }

    if (this.budgetCodes.some((budgetCode) => budgetCode.id === selectedBudgetCode?.id)) {
      this._selectedBudgetCode = this.budgetCodes.find((budgetCode) => budgetCode.id === selectedBudgetCode.id);
      return;
    }

    if (selectedBudgetCode?.id) {
      this.budgetClassificationCodeService
        .findById<BudgetClassificationCodeFindInterface>(selectedBudgetCode.id)
        .subscribe({
          next: (_budgetCode) => {
            if (!this.budgetCodes.some((budgetCode) => budgetCode.id === _budgetCode.id)) {
              this.budgetCodes = this.budgetCodes.concat(_budgetCode);
              this.cdRef.markForCheck();
            }
            setTimeout(() => {
              this._selectedBudgetCode = _budgetCode;
              this.cdRef.markForCheck();
            }, 30);
          }
        });
    }
  }

  @Output() public selectedBudgetCodeChange = new EventEmitter<BudgetClassificationCodeFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = false;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedBudgetCodeId(id: string) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.budgetCodes.some((budgetCode) => budgetCode.id === id)) {
      this._selectedBudgetCode = this.budgetCodes.find((budgetCode) => budgetCode.id === id);
      return;
    }

    this.budgetClassificationCodeService.findById<BudgetClassificationCodeFindInterface>(id).subscribe({
      next: (_budgetCode) => {
        if (!this.budgetCodes.some((budgetCode) => budgetCode.id === _budgetCode.id)) {
          this.budgetCodes = this.budgetCodes.concat(_budgetCode);
          this.cdRef.markForCheck();
        }
        setTimeout(() => {
          this._selectedBudgetCode = _budgetCode;
          this.cdRef.markForCheck();
        }, 30);
      }
    });
  }

  public constructor(
    private readonly budgetClassificationCodeService: BudgetClassificationCodeService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.budgetClassificationCodeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.budgetCodes = this.budgetCodes?.length
            ? this.budgetCodes.concat(
                data.items.filter((item) => !this.budgetCodes.some((budgetCode) => budgetCode.id === item.id))
              )
            : data.items.filter((item) => !this.budgetCodes.some((budgetCode) => budgetCode.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toCode(code: any): BudgetClassificationCodeFindInterface {
    return code as BudgetClassificationCodeFindInterface;
  }

  public onSelectChange(budgetCode: BudgetClassificationCodeFindInterface) {
    this.selectedBudgetCodeChange.emit(budgetCode);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.budgetCodes.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.budgetClassificationCodeService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.budgetCodes = this.budgetCodes.concat(
                    data.items.filter((item) => !this.budgetCodes.some((budgetCode) => budgetCode.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.budgetClassificationCodeService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedBudgetCode?.id) && this._selectedBudgetCode) {
            this.budgetCodes = data.items.concat(this._selectedBudgetCode);
          } else {
            this.budgetCodes = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(code: string): string {
    return HighlightMatchTextHelper.highlightMatchText(code, this.singleSelectComponent?.dropdown?.filterValue);
  }
}
